// Normalising the client API key list.
//
// The API returns masked values only (secrets are never sent back), and the envelope isn't
// pinned down, so this accepts a bare array or a couple of common wrapper keys.

const MASKED_FIELDS = ["masked", "masked_value", "secret_masked", "api_key_masked", "value"];

/**
 * @param {object|object[]} payload Response from GET /admin/client-api-keys.
 * @returns {{name: string, service: string|null, description: string|null,
 *   maskedValue: string|null, updatedAt: string|null}[]}
 */
export const normalizeClientApiKeys = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : payload?.keys ?? payload?.api_keys ?? payload?.client_api_keys ?? payload?.items ?? [];

  return (Array.isArray(list) ? list : [])
    .map((entry) => {
      const masked = MASKED_FIELDS.map((field) => entry?.[field]).find(
        (value) => typeof value === "string" && value.trim() !== ""
      );

      return {
        name: String(entry?.name ?? "").trim(),
        service: entry?.service ?? null,
        description: entry?.description ?? null,
        maskedValue: masked ?? null,
        updatedAt: entry?.updated_at ?? entry?.updatedAt ?? entry?.updated ?? null,
      };
    })
    .filter((key) => key.name);
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The client's public id, from whatever is available.
 *
 * The API keys endpoints are scoped by the client's public id; a numeric database id leaves the
 * server answering "client_id is required". UUID-shaped ids are accepted wherever they appear,
 * then the loaded client list is used to translate a numeric id.
 */
export const resolveClientPublicId = ({ selectedClient = null, activeUser = null, clients = [] } = {}) => {
  const candidates = [
    selectedClient?.public_id,
    selectedClient?.client_public_id,
    activeUser?.client_public_id,
    selectedClient?.id,
    activeUser?.client_id,
  ];

  const uuid = candidates.find((candidate) => typeof candidate === "string" && UUID_PATTERN.test(candidate));
  if (uuid) return uuid;

  const numeric = selectedClient?.id ?? activeUser?.client_id ?? null;
  if (numeric === null || numeric === undefined) return null;

  const match = (Array.isArray(clients) ? clients : []).find(
    (client) => String(client?.id) === String(numeric) || String(client?.public_id) === String(numeric)
  );

  return match?.public_id ?? null;
};

/**
 * The create/update payload for a client API key.
 *
 * The client is identified by its **public id** (never the numeric database id) and omitted for
 * client-admin users. The key material travels as `secret` plus `refresh_token`, with optional
 * expiry timestamps (ISO-8601 UTC).
 *
 * @param {object} input
 * @param {string|null} [input.clientId] The client's public id; omit for client-admins.
 * @param {string} [input.name]
 * @param {string} [input.secret]
 * @param {string} [input.refreshToken]
 * @param {string} [input.service]
 * @param {string} [input.description]
 * @param {string} [input.accessTokenExpiresAt] ISO-8601 UTC.
 * @param {string} [input.refreshTokenExpiresAt] ISO-8601 UTC.
 */
export const buildClientApiKeyPayload = ({
  clientId = null,
  name = "",
  secret = "",
  refreshToken = "",
  service = "",
  description = "",
  accessTokenExpiresAt = "",
  refreshTokenExpiresAt = "",
} = {}) => ({
  ...(clientId ? { client_id: clientId } : {}),
  name,
  secret,
  refresh_token: refreshToken,
  ...(service ? { service } : {}),
  ...(description ? { description } : {}),
  ...(accessTokenExpiresAt ? { access_token_expires_at: accessTokenExpiresAt } : {}),
  ...(refreshTokenExpiresAt ? { refresh_token_expires_at: refreshTokenExpiresAt } : {}),
});

export default normalizeClientApiKeys;
